import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { Overline, CTABand } from "@/components/blocks";
import { ASSETS } from "@/data/site";
import { useContent } from "@/context/ContentContext";

const VALUES = [
  { t: "Experience", d: "Over 25 years caring for women across every stage of life." },
  { t: "Compassion", d: "A warm, patient and reassuring approach to every consultation." },
  { t: "Individualised Care", d: "Guidance tailored to your unique needs and concerns." },
  { t: "Patient Education", d: "Clear explanations that help you make informed decisions." },
];

export default function About() {
  const CLINIC = useContent();
  const physicianSchema = {
    "@context": "https://schema.org",
    "@type": "Physician",
    name: CLINIC.doctorName,
    jobTitle: CLINIC.title,
    medicalSpecialty: ["Obstetric", "Gynecologic"],
    worksFor: { "@type": "MedicalClinic", name: CLINIC.clinicName },
  };

  return (
    <div>
      <SEO title="About Dr. Mithee Bhanot | Obstetrician & Gynaecologist, Noida" path="/about" description="Meet Dr. Mithee Bhanot — M.B.B.S, D.G.O, D.N.B, MRCOG (London), Senior Consultant Obstetrician & Gynaecologist with 25+ years of experience in Noida." />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(physicianSchema) }} />

      <section className="bg-gradient-to-b from-blush to-background pt-28 md:pt-36">
        <div className="container-clinic grid items-center gap-14 pb-20 md:grid-cols-2 md:pb-28">
          <Reveal className="relative order-2 md:order-1">
            <div className="absolute -inset-4 rounded-tr-[5rem] rounded-bl-[5rem] bg-rose/25" aria-hidden="true" />
            <img src={ASSETS.doctorSaree} alt={`${CLINIC.doctorName}`} className="relative w-full rounded-tr-[5rem] rounded-bl-[5rem] object-cover shadow-lift" />
          </Reveal>
          <Reveal delay={120} className="order-1 md:order-2">
            <Overline>Meet Dr. Mithee Bhanot</Overline>
            <h1 className="mt-4 font-serif text-4xl leading-tight tracking-tight text-charcoal sm:text-5xl">
              A trusted name in women's healthcare
            </h1>
            <div className="mt-6 flex flex-wrap gap-2">
              {CLINIC.qualificationList.map((q) => (
                <span key={q} className="rounded-full border border-gold/30 bg-cream px-3 py-1 text-xs font-medium text-terracotta">{q}</span>
              ))}
            </div>
            <p className="mt-3 font-sans text-sm font-medium text-taupe">{CLINIC.title} · 25+ years of experience</p>
            <p className="mt-6 text-base leading-relaxed text-taupe">
              {CLINIC.aboutBio}
            </p>
            <p className="mt-4 text-base leading-relaxed text-taupe">
              She believes that good healthcare begins with listening. Whether you are navigating adolescence, planning a family, expecting a baby or approaching menopause, her practice is built on trust, individualised care and clear communication — so you always feel informed, respected and supported.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-clinic py-20 md:py-28">
        <Reveal><h2 className="max-w-xl font-serif text-3xl text-charcoal md:text-4xl">What guides her practice</h2></Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {VALUES.map((v, i) => (
            <Reveal key={v.t} delay={(i % 2) * 90}>
              <div className="flex gap-4 rounded-3xl border border-soft-border bg-cream p-8">
                <CheckCircle2 className="h-6 w-6 shrink-0 text-terracotta" />
                <div>
                  <h3 className="font-serif text-xl text-charcoal">{v.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-taupe">{v.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-12">
          <Link to="/appointment" data-testid="about-book-btn" className="inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 font-sans text-sm font-semibold text-cream transition-colors hover:bg-terracotta-dark">
            Meet Dr. Mithee <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>

      <CTABand />
    </div>
  );
}
